import React, { useState, useCallback, useRef } from "react";
import "devextreme/dist/css/dx.light.css";
import { Button } from "devextreme-react/button";
import PopUp from "devextreme-react/popup";
import { Popover } from "devextreme-react/popover";
import "./Organization.scss";
import DataReceipt from "../DataReceipt/DataReceipt";
import OrgDetailsToolTip from "../OrganizationToolTip/OrganizationToolTip";
import Notification from "../Notification/Notification";
import { Roles, ApiSuccessMsgs } from "../../Constants/constants";
import { backToOrgSvg, orgInfoSvg } from "../../Assets/SVGs/organizationSVGs";
import EmptyResult from "../EmptyResults/EmptyResult";
import { useUser } from "../../contexts/UserContext";
import OrganizationTree from "../OrganizationTree/OrganizationTree";
import CreateEditOrganization from "../CreateEditOrganization/CreateEditOrganization";
import Environments from "../Environments/Environments";
import EnvironmentTile from "../EnvironmentTile/EnvironmentTile";

const popOverForOrg = {
  class: "popOverForOrg",
};

const Organizations = () => {
  const { userRoles } = useUser();

  const isSuperUser = userRoles?.includes(Roles.SUPER_USER);
  const [envData, setEnvData] = useState();
  const [mode, setMode] = useState("Create");
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isToolTipVisible, setIsToolTipVisible] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [viewDataReceiptPage, setViewDataReceiptPage] = useState(false);
  const [organizationCreated, setOrganizationCreated] = useState(false);
  const [nodeId, setNodeId] = useState(null);
  const treeRef = useRef(null);
  const togglePopup = useCallback(() => {
    setIsPopupVisible(!isPopupVisible);
  }, [isPopupVisible]);

  const handleOpenEditOrganizationPopup = useCallback(() => {
    setIsPopupVisible(true);
    setMode("Edit");
  }, []);

  const toggleToolTipVisibility = useCallback(() => {
    setIsToolTipVisible((prevState) => !prevState);
  }, []);

  const copyAzureGroupID = useCallback(() => {
    navigator.clipboard.writeText(selectedOrg.groupId);
    Notification(ApiSuccessMsgs.AzureADCopy, "success");
  }, [selectedOrg?.groupId]);

  const organizationHierarchyName = (parentId, selectedOrgDataName) => {
    if (parentId === null) {
      return selectedOrgDataName;
    }
    const treeInstance = treeRef?.current?.instance;
    const treeItems = treeInstance?.option("items");
    const current = treeItems.find((x) => x.id === parentId);
    if (current) {
      const parentHierarchy = organizationHierarchyName(current.parentId, current.name);
      return parentHierarchy
        ? `${parentHierarchy} | ${selectedOrgDataName}`
        : selectedOrgDataName;
    }
    return selectedOrgDataName;
  };

  const renderOrgDetailsTooltip = () => (
    <OrgDetailsToolTip
      orgData={selectedOrg}
      organizationHierarchyName={organizationHierarchyName}
      supRole={isSuperUser}
      copyAzureGroupID={copyAzureGroupID}
    />
  );

  const revertToOrganization = useCallback(() => setViewDataReceiptPage(false), []);

  const renderCreatePopup = () => (
    <CreateEditOrganization
      mode={mode}
      setMode={setMode}
      closePopup={togglePopup}
      parentOrgId={selectedOrg?.id}
      currentOrgData={selectedOrg}
      setCurrentOrgData={setSelectedOrg}
      setOrganizationCreated={setOrganizationCreated}
    />
  );

  const getClickableLinkForOrg = (link) => {
    if (link.startsWith("https://")) {
      return link;
    }
    return `//${link}`;
  };

  const renderEnvironmentTileItem = () => (
    <EnvironmentTile
      orgData={selectedOrg}
      toggleEditPopupForPartner={handleOpenEditOrganizationPopup}
      renderToolTip={renderOrgDetailsTooltip}
      getClickableLinkForOrg={getClickableLinkForOrg}
    />
  );

  const emptyResult = () => <EmptyResult content="No organization selected!" />;

  return (
    <div className="organization-content-wrapper">
      <div
        className={viewDataReceiptPage ? "d-none" : "d-block"}
        id="organization-div-content"
      >
        <div className="organization-list d-flex h-100">
          <OrganizationTree
            setIsCreateOrgPopupVisibleCallback={setIsPopupVisible}
            setSelectedOrgCallback={setSelectedOrg}
            organizationCreated={organizationCreated}
            setOrganizationCreated={setOrganizationCreated}
            setNodeId={setNodeId}
            nodeId={nodeId}
            ref={treeRef}
          />
          <div className="environment-data w-100">
            {selectedOrg != null ? (
              <div>
                <div className="environmentTile">
                  {selectedOrg?.id !== undefined
                    ? renderEnvironmentTileItem()
                    : emptyResult()}
                </div>

                <Environments
                  orgData={selectedOrg}
                  viewDataReceiptPage={viewDataReceiptPage}
                  setViewDataReceiptPage={setViewDataReceiptPage}
                  setEnvData={setEnvData}
                />
              </div>
            ) : (
              <EmptyResult content="No organization selected!" />
            )}
          </div>
        </div>
        <div className="environmentList" />
      </div>
      <div
        className={viewDataReceiptPage ? "d-block" : "d-none"}
        id="organization-div-content"
      >
        {viewDataReceiptPage && (
          <div className=" d-flex flex-column">
            <div className="org-details-title">
              <span className="org-back">
                <Button onClick={revertToOrganization}>{backToOrgSvg}</Button>
              </span>
              <span className="org-Name">{selectedOrg?.name}</span>
              <span className="org-Info">
                <Button onClick={toggleToolTipVisibility} stylingMode="text">
                  {" "}
                  {orgInfoSvg(isToolTipVisible)}
                </Button>
                <Popover
                  className="popOverForOrg"
                  wrapperAttr={popOverForOrg}
                  target="#organizationsvgs-svg-info"
                  contentRender={renderOrgDetailsTooltip}
                  width={484}
                  visible={isToolTipVisible}
                  onHiding={toggleToolTipVisibility}
                />
              </span>
            </div>
            <div className="">
              <DataReceipt envData={envData} />
            </div>
          </div>
        )}
      </div>
      <PopUp
        visible={isPopupVisible}
        position="center"
        maxHeight={440}
        maxWidth={920}
        showTitle={false}
        showCloseButton={false}
        contentRender={renderCreatePopup}
        wrapperAttr={{ class: "create-edit-organization-popup-wrapper" }}
      />
    </div>
  );
};
export default Organizations;
